/**
 * PasswordStrengthMeter — visual bar for AUTH-04 and AUTH-05.
 *
 * Score is derived from three required criteria (length ≥8, mixed case, digit)
 * plus one bonus criterion (symbol). Segment colours progress red → amber →
 * green → green as score improves. A short hint label explains which criterion
 * is still missing.
 * No external dependencies — pure heuristic, zero weight.
 */

interface PasswordStrengthMeterProps {
  password: string;
}

const CHECKS: Array<(p: string) => boolean> = [
  (p) => p.length >= 8,
  (p) => /[a-z]/.test(p) && /[A-Z]/.test(p),
  (p) => /\d/.test(p),
  (p) => /[^a-zA-Z0-9]/.test(p),
];

// Fixed colour per segment — each bar has an intrinsic meaning as you fill more.
const SEGMENT_COLORS = [
  'var(--status-red)',
  'var(--status-amber)',
  'var(--status-green)',
  'var(--status-green)',
] as const;

const LABELS = ['', 'Weak', 'Fair', 'Good', 'Strong'] as const;
const MISSING_HINTS = [
  'make it 8+ characters',
  'mix upper & lowercase letters',
  'add a number',
  'add a symbol for extra strength',
] as const;

export function PasswordStrengthMeter({ password }: PasswordStrengthMeterProps) {
  if (!password) return null;

  const results = CHECKS.map((check) => check(password));
  const score = results.filter(Boolean).length as 0 | 1 | 2 | 3 | 4;
  const label = LABELS[score];
  const missingIndex = results.findIndex((r) => !r);
  const hint = missingIndex >= 0 ? `${label} — ${MISSING_HINTS[missingIndex]}` : label;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2">
        {SEGMENT_COLORS.map((color, i) => (
          <div
            key={i}
            className="h-1.5 flex-1 rounded-full transition-colors duration-150"
            style={{
              backgroundColor: i < score ? color : 'var(--bg-surface-raised)',
            }}
          />
        ))}
      </div>
      {hint && <p className="text-[11px] text-[var(--text-secondary)]">{hint}</p>}
    </div>
  );
}
